/**
 * Helpers for the Documents Management page.
 *
 * Kept out of the components for the same reason reportHelpers.js is: the
 * company table, the two tabs and the dangling section all need the same
 * answers, and a shared module is easier to reason about than a mixin.
 */

/**
 * formatFileSize
 * Byte counts as they are worth reading in a table. `size` is written by the
 * backend at upload time; records that predate it have none.
 *
 * @param {number|null} bytes
 * @returns {string}
 */
export function formatFileSize (bytes) {
  const value = Number(bytes)
  if (!isFinite(value) || value <= 0) return '-'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}


/**
 * companyMatchesSearch
 * Free-text match over the fields a broker would actually type: the company's
 * name, its CNPJ and the broker it hangs under.
 *
 * @param {Object} company a row from get-document-reports
 * @param {string} search
 * @returns {boolean}
 */
export function companyMatchesSearch (company, search) {
  const needle = (search || '').trim().toLowerCase()
  if (!needle) return true

  return [company.name, company.cnpj, company.broker_name, company.broker_group, company.company_id]
    .some(field => String(field || '').toLowerCase().includes(needle))
}

/**
 * triggerBrowserDownload
 * Hands a fetched blob to the browser as a file. The backend already sets a
 * Content-Disposition, but an XHR response never reaches the download manager
 * on its own, so the object URL has to be built and clicked here.
 *
 * @param {Blob} blob
 * @param {string} filename
 */
export function triggerBrowserDownload (blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'document.pdf'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
